import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { problemText, toApiError, useApi, type CreateUpgradeChargeRequest } from '@/shared/api';

const key = (bookingId: string) => ['bookings', 'upgrade-charges', bookingId] as const;

export function useUpgradeCharges(bookingId: string, enabled = true) {
  const api = useApi();
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: key(bookingId) });
  const query = useQuery({
    queryKey: key(bookingId),
    queryFn: () => api.bookings.upgradeCharges(bookingId),
    enabled,
    refetchInterval: (state) =>
      state.state.data?.some((charge) =>
        ['PaymentStarted', 'CancellationPending'].includes(charge.status),
      )
        ? 3_000
        : false,
  });
  const request = useMutation({
    mutationFn: async (body: CreateUpgradeChargeRequest) => {
      const draft = await api.bookings.createUpgradeCharge(bookingId, body);
      return api.bookings.sendUpgradeCharge(bookingId, draft.upgradeChargeId);
    },
    onSuccess: () => toast.success('Aufpreis-Link wurde gesendet'),
    onError: showError,
    onSettled: refresh,
  });
  const send = useMutation({
    mutationFn: (chargeId: string) => api.bookings.sendUpgradeCharge(bookingId, chargeId),
    onSuccess: () => toast.success('Aufpreis-Link wurde erneut gesendet'),
    onError: showError,
    onSettled: refresh,
  });
  const cancel = useMutation({
    mutationFn: (chargeId: string) => api.bookings.cancelUpgradeCharge(bookingId, chargeId),
    onSuccess: () => toast.success('Aufpreis wird aufgehoben'),
    onError: showError,
    onSettled: refresh,
  });
  return { query, request, send, cancel };
}

const ERRORS: Record<string, string> = {
  stripe_not_connected: 'Stripe ist nicht verbunden – ohne Stripe gibt es keinen Zahlungslink.',
  upgrade_charge_guest_email_missing:
    'Am Gast fehlt eine E-Mail-Adresse – der Zahlungslink kann nicht verschickt werden.',
  upgrade_charge_booking_status_invalid: 'In diesem Status der Buchung ist kein Aufpreis möglich.',
  upgrade_charge_cancel_forbidden: 'Dieser Aufpreis lässt sich nicht mehr aufheben.',
  upgrade_charge_currency_mismatch: 'Die Währung passt nicht zur Buchung.',
};

async function showError(error: unknown) {
  toast.error(
    problemText(await toApiError(error), 'Der Aufpreis konnte nicht bearbeitet werden.', {
      known: ERRORS,
    }),
  );
}
